import defaultPolicies from '../../configs/defaultPolicies.json';
import {remoteConfig, fetchAndActivate, getValue} from '../../setupFirebase';

const DEFAULT_VALUES = {
  boolean: false,
  string: '',
  number: 0,
  json: {}
};

const FALLBACK_CONFIGS = {
  terms_policy_content: defaultPolicies
};

const ERROR_CODES = {
  STORAGE_BLOCKED: 'remoteconfig/storage-open',
  FETCH_THROTTLE: 'remoteconfig/fetch-throttle',
  TIMEOUT: 'remoteconfig/timeout'
};

const CACHE_DURATION_MS = 60000;
const isDevelopment = () => process.env.NODE_ENV === 'development';

const logger = {
  error: (context, error) => {
    if (isDevelopment()) {
      console.error(`[RemoteConfig] ${context}:`, error);
    }
  },
  info: (message) => {
    if (isDevelopment()) {
      console.log(`[RemoteConfig] ${message}`);
    }
  }
};

/**
 * Gerencia o ciclo de vida do Firebase Remote Config
 * Implementa cache, retry logic e fallback para valores locais
 */
class RemoteConfigManager {
  constructor() {
    this.listeners = new Set();
    this.lastFetchTime = 0;
    this.isActivated = false;
    this.initializePromise = null;
  }

  isCacheValid() {
    return Date.now() - this.lastFetchTime < CACHE_DURATION_MS;
  }

  isStorageBlocked(error) {
    return error?.code === ERROR_CODES.STORAGE_BLOCKED;
  }

  isThrottled(error) {
    return error?.code === ERROR_CODES.FETCH_THROTTLE;
  }

  isRecoverableError(error) {
    return this.isStorageBlocked(error) || this.isThrottled(error);
  }

  /**
   * Busca e ativa configurações do Firebase Remote Config
   * @returns {Promise<boolean>} true se novos valores foram ativados
   */
  async fetchAndActivateConfig() {
    try {
      const hasNewValues = await fetchAndActivate(remoteConfig);
      this.isActivated = true;
      this.lastFetchTime = Date.now();
      logger.info(hasNewValues ? 'Ativado' : 'Cache');
      return hasNewValues;
    } catch (error) {
      if (this.isRecoverableError(error)) {
        logger.info('Usando fallback');
        this.lastFetchTime = Date.now();
        return false;
      }
      logger.error('Fetch', error);
      throw error;
    }
  }

  async fetchIfNeeded() {
    if (this.isCacheValid()) {
      return false;
    }

    try {
      return await this.fetchAndActivateConfig();
    } catch (error) {
      logger.error('FetchIfNeeded', error);
      return false;
    }
  }

  notifyListeners() {
    this.listeners.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        logger.error('Listener', error);
      }
    });
  }

  /**
   * Inscreve um callback para mudanças de configuração
   * @param {Function} callback - Função a ser chamada quando config mudar
   * @returns {Function} Função para cancelar inscrição
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  async refresh() {
    const hasUpdates = await this.fetchIfNeeded();
    if (hasUpdates) {
      this.notifyListeners();
    }
    return hasUpdates;
  }

  cleanup() {
    this.listeners.clear();
    this.initializePromise = null;
  }
}

const configManager = new RemoteConfigManager();

const setupAutoRefresh = () => {
  if (typeof window === 'undefined') return;

  const handleFocus = () => configManager.refresh();
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      configManager.refresh();
    }
  };

  window.addEventListener('focus', handleFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('focus', handleFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

const cleanupAutoRefresh = setupAutoRefresh();

/**
 * Extrai valor do Remote Config com fallback
 * @private
 */
const getConfigValue = (key) => {
  if (!configManager.isActivated) {
    return {value: null, source: 'fallback'};
  }

  try {
    const valueObj = getValue(remoteConfig, key);
    return {
      value: valueObj.asString(),
      source: valueObj.getSource()
    };
  } catch (error) {
    logger.error('getValue', error);
    return {value: null, source: 'error'};
  }
};

/**
 * Serviço público para acesso ao Firebase Remote Config
 */
export const remoteConfigService = {
  /**
   * Inicializa o Remote Config (idempotente)
   * @returns {Promise<boolean>} true se inicializado com sucesso
   */
  async initialize() {
    if (configManager.initializePromise) {
      return configManager.initializePromise;
    }

    configManager.initializePromise = (async () => {
      try {
        await configManager.fetchAndActivateConfig();
        return true;
      } catch (error) {
        if (configManager.isRecoverableError(error)) {
          return true;
        }
        logger.error('Initialize', error);
        return false;
      }
    })();

    return configManager.initializePromise;
  },

  getBoolean(key) {
    try {
      return getValue(remoteConfig, key).asBoolean();
    } catch (error) {
      logger.error('getBoolean', error);
      return DEFAULT_VALUES.boolean;
    }
  },

  getString(key) {
    try {
      return getValue(remoteConfig, key).asString();
    } catch (error) {
      logger.error('getString', error);
      return DEFAULT_VALUES.string;
    }
  },

  getNumber(key) {
    try {
      return getValue(remoteConfig, key).asNumber();
    } catch (error) {
      logger.error('getNumber', error);
      return DEFAULT_VALUES.number;
    }
  },

  /**
   * Obtém valor JSON do Remote Config com fallback
   * @param {string} key - Chave da configuração
   * @returns {Object} Objeto JSON parseado ou fallback
   */
  getJSON(key) {
    try {
      const {value} = getConfigValue(key);

      if (!value) {
        return FALLBACK_CONFIGS[key] || DEFAULT_VALUES.json;
      }

      return JSON.parse(value);
    } catch (error) {
      logger.error('getJSON', error);
      return FALLBACK_CONFIGS[key] || DEFAULT_VALUES.json;
    }
  },

  onConfigChange(callback) {
    return configManager.subscribe(callback);
  },

  async refresh() {
    return configManager.refresh();
  },

  /**
   * Limpa recursos (para testes)
   * @private
   */
  _cleanup() {
    if (cleanupAutoRefresh) {
      cleanupAutoRefresh();
    }
    configManager.cleanup();
  }
};
